import React, { useState, useEffect } from "react";
import { RadarSpeciesDto, useBirdRadarStore } from "../store/RadarStore";
import { UI_TEXT } from "../App";

interface SpeciesCardProps {
  bird: RadarSpeciesDto;
}

export default function SpeciesCard({ bird }: SpeciesCardProps) {
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [activeTab, setActiveTab] = useState<"info" | "sounds" | "colors">("info");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const language = useBirdRadarStore(s => s.language);
  const t = UI_TEXT[language] || UI_TEXT["en"];

  const hasColors = bird.themeColors && bird.themeColors.length > 0;

  const primaryColor = bird.themeColors?.[0] || "#3b82f6";
  const secondaryColor = bird.themeColors?.[1] || primaryColor;
  const hasPhotos = bird.topPhotos && bird.topPhotos.length > 0;
  const hasSounds = bird.topSounds && bird.topSounds.length > 0;

  // Translate language codes → flag/label
  const langLabels: Record<string, string> = {
    en: "EN", es: "ES", pt: "PT", fr: "FR", de: "DE", zh: "ZH"
  };

  const displayName = bird.translations?.[language] || bird.primaryCommonName;

  // Lock body scroll when fullscreen modal is open
  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isFullScreen]);

  // Find most recent observation date
  let latestDateStr = "";
  if (bird.sightings && bird.sightings.length > 0) {
    const dates = bird.sightings.map(s => new Date(s.observationDate).getTime());
    const maxDate = new Date(Math.max(...dates));
    
    // Format appropriately: "Apr 9, 2026, 11:30 AM" or localized equivalent
    latestDateStr = maxDate.toLocaleDateString(language, { 
      year: 'numeric', month: 'short', day: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });
  }

  return (
    <>
    <div
      className="rounded-2xl overflow-hidden shadow-xl bg-neutral-900 border border-neutral-800 flex-shrink-0"
      style={{ boxShadow: `0 4px 24px ${primaryColor}33, 0 1px 4px rgba(0,0,0,0.6)` }}
    >
      {/* PHOTO SECTION */}
      <div className="relative w-full h-52 bg-neutral-800 overflow-hidden">
        {hasPhotos ? (
          <>
            <img
              src={bird.topPhotos[activePhotoIdx].imageUrl}
              alt={bird.primaryCommonName}
              className="w-full h-full object-cover transition-opacity duration-300 cursor-pointer"
              onClick={() => setIsFullScreen(true)}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  `https://placehold.co/400x220/1a1a2e/6366f1?text=${encodeURIComponent(bird.primaryCommonName)}`;
              }}
            />
            {/* Photo Navigation Dots */}
            {bird.topPhotos.length > 1 && (
              <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                {bird.topPhotos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePhotoIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === activePhotoIdx ? "bg-white scale-125" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            )}
            {/* Photo Attribution */}
            {bird.topPhotos[activePhotoIdx].ownerName && (
              <div className="absolute top-2 right-2 bg-black/50 px-2 py-0.5 rounded text-[10px] text-white/70">
                © {bird.topPhotos[activePhotoIdx].ownerName}
              </div>
            )}
          </>
        ) : (
          /* Fallback: Gradient placeholder with species initials */
          <div
            className="w-full h-full flex flex-col items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${primaryColor}44, ${secondaryColor}22)` }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-black"
              style={{ background: `${primaryColor}55`, color: primaryColor }}
            >
              {bird.primaryCommonName.charAt(0)}
            </div>
            <span className="text-xs text-neutral-500">No image available</span>
          </div>
        )}

        {/* Color Palette as a gradient bar */}
        {bird.themeColors?.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 flex">
            {bird.themeColors.map((color, i) => (
              <div key={i} className="flex-1" style={{ backgroundColor: color }} />
            ))}
          </div>
        )}
      </div>

      {/* HEADER */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold leading-tight truncate">{displayName}</h2>
            <p className="text-xs italic text-neutral-500 font-mono mt-0.5 truncate">{bird.scientificName}</p>
          </div>
          {/* Species Code Badge */}
          <span
            className="text-xs font-mono font-bold px-2 py-1 rounded-lg flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}22`, color: primaryColor }}
          >
            {bird.speciesCode}
          </span>
        </div>

        {/* Translations row */}
        {bird.translations && Object.keys(bird.translations).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {Object.entries(bird.translations)
              .filter(([, v]) => v)
              .map(([lang, name]) => (
                <span
                  key={lang}
                  className="text-[11px] px-2 py-0.5 bg-neutral-800 rounded-full text-neutral-300"
                >
                  <span className="text-neutral-500 mr-1">{langLabels[lang] || lang}</span>
                  {name}
                </span>
              ))}
          </div>
        )}
      </div>

      {/* TAB BAR — always visible */}
      <div className="flex border-t border-neutral-800 text-xs font-semibold">
        {(["info", "sounds", "colors"] as const).map((tab) => {
          // Skip sounds tab if none available
          if (tab === "sounds" && !hasSounds) return null;
          const labels = { info: "📋 Info", sounds: "🔊 Sounds", colors: "🎨 Colors" };
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 capitalize transition-colors ${
                activeTab === tab
                  ? "text-white border-b-2"
                  : "text-neutral-500 hover:text-neutral-300"
              }`}
              style={activeTab === tab ? { borderColor: primaryColor } : {}}
            >
              {labels[tab]}
            </button>
          );
        })}
      </div>

      {/* TAB CONTENT */}
      <div className="px-4 py-3 min-h-[60px]">
        {activeTab === "info" && (
          <div className="space-y-1 text-xs text-neutral-400">
            <p><span className="text-neutral-500">{t.infoSpeciesId}:</span> {bird.speciesId}</p>
            {latestDateStr && (
              <p><span className="text-neutral-500">{t.infoLastSeen}:</span> {latestDateStr}</p>
            )}
            <p><span className="text-neutral-500">{t.infoPhotos}:</span> {bird.topPhotos?.length ?? 0} {t.infoAvailable}</p>
            <p><span className="text-neutral-500">{t.infoSounds}:</span> {bird.topSounds?.length ?? 0} {t.infoRecordings}</p>
          </div>
        )}

        {activeTab === "sounds" && hasSounds && (
          <div className="space-y-2">
            {bird.topSounds.map((sound, idx) => (
              <div key={idx} className="bg-neutral-800/60 rounded-xl p-2">
                <div className="flex items-center justify-between text-[11px] mb-1.5">
                  <span className="text-neutral-300 font-medium capitalize">{sound.soundType || "Song"}</span>
                  <span
                    className="px-1.5 py-0.5 rounded font-mono font-bold"
                    style={{ backgroundColor: `${primaryColor}22`, color: primaryColor }}
                  >
                    {sound.quality}
                  </span>
                </div>
                <audio
                  controls
                  className="w-full h-7"
                  src={sound.audioUrl}
                  preload="none"
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === "colors" && (
          <div className="space-y-3">
            {/* Full gradient preview */}
            {hasColors && (
              <div
                className="w-full h-8 rounded-lg overflow-hidden"
                style={{
                  background: `linear-gradient(to right, ${bird.themeColors.join(", ")})`
                }}
              />
            )}

            {/* Individual swatches */}
            <div className="flex flex-col gap-2">
              {(bird.themeColors ?? []).map((hex, i) => {
                // Parse hex → RGB → approx HSL label
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                const rn = r / 255, gn = g / 255, bn = b / 255;
                const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
                const l = Math.round(((max + min) / 2) * 100);
                const s = max === min ? 0 : Math.round(
                  ((max - min) / (l < 50 ? max + min : 2 - max - min)) * 100
                );

                return (
                  <div key={i} className="flex items-center gap-3">
                    {/* Swatch */}
                    <div
                      className="w-10 h-10 rounded-xl flex-shrink-0 shadow-md"
                      style={{ backgroundColor: hex }}
                    />
                    {/* Labels */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-white">{hex.toUpperCase()}</span>
                        <span className="text-[10px] text-neutral-500">#{i + 1}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        RGB({r}, {g}, {b}) &nbsp;·&nbsp; L:{l}% S:{s}%
                      </div>
                    </div>
                    {/* Copy button */}
                    <button
                      onClick={() => navigator.clipboard?.writeText(hex.toUpperCase())}
                      className="text-[10px] text-neutral-500 hover:text-white transition px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700"
                    >
                      Copy
                    </button>
                  </div>
                );
              })}
            </div>

            {!hasColors && (
              <p className="text-xs text-neutral-500 text-center py-2">No color data extracted yet.</p>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Fullscreen Photo Dialog */}
    {isFullScreen && hasPhotos && (
      <div 
        className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm cursor-pointer"
        onClick={() => setIsFullScreen(false)}
      >
        {/* Close Button */}
        <div className="absolute top-4 right-4 z-[110]">
          <button 
            className="bg-white/10 hover:bg-white/20 text-white rounded-full w-10 h-10 flex items-center justify-center backdrop-blur-md transition shadow-lg border border-white/20"
            onClick={() => setIsFullScreen(false)}
          >
            ✕
          </button>
        </div>
        
        {/* Image Container */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-12 relative w-full h-full overflow-hidden">
           <img 
             src={bird.topPhotos[activePhotoIdx].imageUrl} 
             className="max-w-full max-h-full object-contain drop-shadow-2xl" 
             alt={bird.primaryCommonName}
             onClick={(e) => e.stopPropagation()}
           />
           
           {/* Navigation arrows (only if multiple photos) */}
           {bird.topPhotos.length > 1 && (
             <>
               <button 
                  className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 lg:p-4 bg-black/50 hover:bg-black/80 rounded-full text-white backdrop-blur-md transition shadow-lg border border-white/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIdx((prev) => prev === 0 ? bird.topPhotos.length - 1 : prev - 1);
                  }}
               >
                  ◀
               </button>
               <button 
                  className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 lg:p-4 bg-black/50 hover:bg-black/80 rounded-full text-white backdrop-blur-md transition shadow-lg border border-white/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePhotoIdx((prev) => prev === bird.topPhotos.length - 1 ? 0 : prev + 1);
                  }}
               >
                  ▶
               </button>
             </>
           )}
        </div>
        
        {/* Footer Meta */}
        <div 
          className="p-4 md:p-6 text-center text-white/80 absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pointer-events-none"
        >
           <h2 className="text-xl md:text-2xl font-bold text-white drop-shadow-md">{displayName}</h2>
           <p className="text-sm italic drop-shadow-md">{bird.scientificName}</p>
           {bird.topPhotos[activePhotoIdx].ownerName && (
             <p className="text-xs mt-2 text-white/50">© {bird.topPhotos[activePhotoIdx].ownerName}</p>
           )}
        </div>
      </div>
    )}
    </>
  );
}
